import React from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { PALETTE, idrShort, idrFull, num, useDash } from './dashUtils';

const fmtVal = (v, kind) => (kind === 'idr' ? idrFull(v) : num(v));
const useTheme = () => {
  const { dark } = useDash();
  return dark
    ? { axis: { fontSize: 11, fill: '#94A3B8' }, grid: <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />, tipStyle: { borderRadius: 12, border: '1px solid #334155', background: '#0F172A', color: '#E2E8F0', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', fontSize: 12 }, stroke: '#0F172A', cursor: 'rgba(148,163,184,0.15)' }
    : { axis: { fontSize: 11, fill: '#64748B' }, grid: <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />, tipStyle: { borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 10px 30px rgba(16,24,40,0.10)', fontSize: 12 }, stroke: '#fff', cursor: 'rgba(148,163,184,0.12)' };
};

export const Empty = ({ text }) => <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-muted-foreground">{text}</div>;

export const ChartBox = ({ title, children, className = '', testid, h = 240 }) => (
  <div className={`dash-fade rounded-2xl border border-border bg-white p-4 shadow-soft ${className}`} data-testid={testid}>
    <div className="mb-2 text-sm font-semibold text-slate-700">{title}</div>
    <div style={{ height: h }}>{children}</div>
  </div>
);

/** Line trend with optional dashed previous-period series. */
export const TrendLine = ({ data, color, kind = 'idr', valueKey = 'value', prevKey, lang = 'id' }) => {
  const { axis, grid, tipStyle } = useTheme();
  if (!data.length || !data.some((d) => d[valueKey] || (prevKey && d[prevKey]))) return <Empty text={lang === 'id' ? 'Belum ada data pada periode ini' : 'No data for this period'} />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        {grid}
        <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => (kind === 'idr' ? idrShort(v).replace('Rp ', '') : num(v))} />
        <Tooltip contentStyle={tipStyle} formatter={(v, n) => [fmtVal(v, kind), n]} />
        {prevKey && <Line type="monotone" dataKey={prevKey} name={lang === 'id' ? 'Periode sebelumnya' : 'Previous period'} stroke="#94A3B8" strokeDasharray="5 4" strokeWidth={1.5} dot={false} isAnimationActive />}
        <Line type="monotone" dataKey={valueKey} name={lang === 'id' ? 'Periode ini' : 'This period'} stroke={color} strokeWidth={2.5} dot={data.length <= 31} activeDot={{ r: 5 }} isAnimationActive animationDuration={1200} />
        {prevKey && <Legend wrapperStyle={{ fontSize: 11 }} />}
      </LineChart>
    </ResponsiveContainer>
  );
};

export const Bars = ({ data, series, kind = 'idr', lang = 'id', stacked = false }) => {
  const { axis, grid, tipStyle, cursor } = useTheme();
  if (!data.length || !data.some((d) => series.some((s) => d[s.key]))) return <Empty text={lang === 'id' ? 'Belum ada data pada periode ini' : 'No data for this period'} />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
        {grid}
        <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={kind === 'idr' ? 64 : 40} tickFormatter={(v) => (kind === 'idr' ? idrShort(v).replace('Rp ', '') : num(v))} />
        <Tooltip contentStyle={tipStyle} cursor={{ fill: cursor }} formatter={(v, n) => [fmtVal(v, kind), n]} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {series.map((s) => <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[6, 6, 0, 0]} stackId={stacked ? 'a' : undefined} isAnimationActive animationDuration={1000} />)}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const AreaTrend = ({ data, color, valueKey = 'balance', kind = 'idr', lang = 'id' }) => {
  const { axis, grid, tipStyle } = useTheme();
  if (!data.length) return <Empty text={lang === 'id' ? 'Belum ada data' : 'No data'} />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs><linearGradient id={`g-${valueKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.45} /><stop offset="100%" stopColor={color} stopOpacity={0.03} /></linearGradient></defs>
        {grid}
        <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => idrShort(v).replace('Rp ', '')} />
        <Tooltip contentStyle={tipStyle} formatter={(v) => [fmtVal(v, kind), lang === 'id' ? 'Saldo' : 'Balance']} />
        <Area type="monotone" dataKey={valueKey} stroke={color} strokeWidth={2.5} fill={`url(#g-${valueKey})`} isAnimationActive animationDuration={1200} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/** Donut with legend list on the right. */
export const Donut = ({ data, kind = 'num', lang = 'id', colors = PALETTE }) => {
  const { tipStyle, stroke } = useTheme();
  const rows = data.filter((d) => d.value > 0);
  const total = rows.reduce((a, b) => a + b.value, 0);
  if (!total) return <Empty text={lang === 'id' ? 'Belum ada data' : 'No data'} />;
  return (
    <div className="flex h-full items-center gap-3">
      <div className="h-full w-[42%] min-w-[110px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="label" innerRadius="58%" outerRadius="88%" paddingAngle={2} stroke={stroke} isAnimationActive animationDuration={1000}>
              {rows.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip contentStyle={tipStyle} formatter={(v, n) => [fmtVal(v, kind), n]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="flex-1 space-y-1.5 text-xs">
        {rows.map((r, i) => (
          <li key={r.key || r.label} className="flex items-center justify-between gap-2" data-testid={`donut-${r.key || i}`}>
            <span className="flex min-w-0 items-start gap-1.5"><span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colors[i % colors.length] }} /><span className="break-words text-slate-600">{r.label}</span></span>
            <span className="shrink-0 text-right font-semibold text-slate-800">{fmtVal(r.value, kind)}<br /><span className="font-normal text-muted-foreground">{Math.round((r.value / total) * 100)}%</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Top-N horizontal bars (products). */
export const TopBars = ({ items, color, lang = 'id', testid }) => {
  if (!items?.length) return <Empty text={lang === 'id' ? 'Belum ada penjualan produk' : 'No product sales yet'} />;
  const max = Math.max(...items.map((i) => i.amount)) || 1;
  return (
    <ul className="flex h-full flex-col justify-center gap-2.5" data-testid={testid}>
      {items.map((it, i) => (
        <li key={it.name} className="dash-fade" style={{ animationDelay: `${i * 80}ms` }}>
          <div className="mb-1 flex items-center justify-between gap-2 text-xs"><span className="flex min-w-0 items-center gap-2"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-md text-[10px] font-bold text-white" style={{ background: color }}>{i + 1}</span><span className="truncate font-medium text-slate-700">{it.name}</span></span><span className="shrink-0 text-muted-foreground">{it.qty > 0 && <>{num(it.qty)} unit · </>}<b className="text-slate-800">{idrShort(it.amount)}</b></span></div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="dash-grow h-full rounded-full" style={{ width: `${(it.amount / max) * 100}%`, background: color, animationDelay: `${i * 80 + 150}ms` }} /></div>
        </li>
      ))}
    </ul>
  );
};
